import { buildQuery, requestJson } from './client'

export type LabelCategory = 'genre' | 'mood' | 'use_case' | 'instrument' | 'texture' | 'rhythm' | 'tempo' | 'energy' | 'vocal_presence' | 'language'
export type LabelStatus = 'active' | 'archived'

export type Label = {
  id: string
  category: LabelCategory
  name: string
  slug: string
  description: string | null
  aliases: string[]
  status: LabelStatus
  track_count: number
  created_at: string
  updated_at: string
}

export type AudioTrackLabelSummary = {
  id: string
  category: LabelCategory
  name: string
  slug: string
  source: string
  status: string
}

export type AudioAnalysisStatus = 'never_run' | 'queued' | 'processing' | 'completed' | 'failed'

export type AudioAnalysisSummary = {
  status: AudioAnalysisStatus
  completed_count: number
  latest_run_id: string | null
  latest_completed_at: string | null
  latest_failed_at: string | null
  pending_job_id: string | null
  last_error: string | null
  pending_suggestion_count: number
  rejected_suggestion_count: number
}

export type AudioTrackLabel = {
  id: string
  audio_track_id: string
  label_id: string
  source: string
  status: string
  confidence: string | null
  weight: string
  reason: string | null
  label: Pick<Label, 'id' | 'category' | 'name' | 'slug' | 'description' | 'aliases' | 'status'>
  created_at: string
  updated_at: string
}

export type AudioTrackLabelSuggestion = {
  audio_track_id: string | null
  label_id: string
  source: 'prompt_llm' | 'audio_ai'
  status: 'suggested'
  confidence: number
  weight: number
  reason: string
  label: Pick<Label, 'id' | 'category' | 'name' | 'slug' | 'description' | 'aliases' | 'status'>
}

export type AudioTrackLabelSaveInput = {
  labelId: string
  source: 'manual' | 'prompt_llm' | 'audio_local' | 'audio_ai'
  confidence?: number | null
  weight?: number | null
  reason?: string | null
}

export type AudioTrackLabelSuggestionMeta = {
  audio_track_id: string | null
  cache_hit?: boolean
  candidate_label_count: number
  model: string
  provider_request_id: string | null
  usage: {
    promptTokens: number | null
    completionTokens: number | null
    totalTokens: number | null
  }
}

export type AudioTrackLabelSuggestionResult = {
  items: AudioTrackLabelSuggestion[]
  meta: AudioTrackLabelSuggestionMeta
}

export type AudioTrackLabelStateResult = {
  items: AudioTrackLabel[]
  audio_analysis: AudioAnalysisSummary
}

export type LabelFilters = {
  category?: LabelCategory | undefined
  status?: LabelStatus | undefined
  search?: string | undefined
}

export const getLabels = async (filters: LabelFilters = {}): Promise<Label[]> => {
  const payload = await requestJson<{ items: Label[] }>(
    `/api/admin/labels${buildQuery(filters)}`
  )

  return payload.items
}

export const createLabel = async (input: {
  category: LabelCategory
  name: string
  slug: string
  description: string | null
  aliases: string[]
}): Promise<Label> => {
  const payload = await requestJson<{ item: Label }>('/api/admin/labels', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(input)
  })

  return payload.item
}

export const updateLabel = async (input: {
  id: string
  category: LabelCategory
  name: string
  slug: string
  description: string | null
  aliases: string[]
  status: LabelStatus
}): Promise<Label> => {
  const payload = await requestJson<{ item: Label }>(
    `/api/admin/labels/${encodeURIComponent(input.id)}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(input)
    }
  )

  return payload.item
}

export const archiveLabel = async (labelId: string): Promise<Label> => {
  const payload = await requestJson<{ item: Label }>(
    `/api/admin/labels/${encodeURIComponent(labelId)}/archive`,
    {
      method: 'POST'
    }
  )

  return payload.item
}

export const getAudioTrackLabels = async (audioTrackId: string): Promise<AudioTrackLabelStateResult> => {
  return requestJson<AudioTrackLabelStateResult>(
    `/api/admin/audio-tracks/${encodeURIComponent(audioTrackId)}/labels`
  )
}

export const updateAudioTrackLabels = async (
  audioTrackId: string,
  labels: AudioTrackLabelSaveInput[]
): Promise<AudioTrackLabel[]> => {
  const payload = await requestJson<{ items: AudioTrackLabel[] }>(
    `/api/admin/audio-tracks/${encodeURIComponent(audioTrackId)}/labels`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ labels })
    }
  )

  return payload.items
}

export const acceptAudioTrackLabel = async (
  audioTrackId: string,
  assignmentId: string
): Promise<AudioTrackLabel> => {
  const payload = await requestJson<{ item: AudioTrackLabel }>(
    `/api/admin/audio-tracks/${encodeURIComponent(audioTrackId)}/labels/${encodeURIComponent(assignmentId)}/accept`,
    {
      method: 'POST'
    }
  )

  return payload.item
}

export const rejectAudioTrackLabel = async (
  audioTrackId: string,
  assignmentId: string
): Promise<AudioTrackLabel> => {
  const payload = await requestJson<{ item: AudioTrackLabel }>(
    `/api/admin/audio-tracks/${encodeURIComponent(audioTrackId)}/labels/${encodeURIComponent(assignmentId)}/reject`,
    {
      method: 'POST'
    }
  )

  return payload.item
}

export const suggestAudioTrackLabels = async (input: {
  audioTrackId: string
  style_prompt: string
}): Promise<AudioTrackLabelSuggestionResult> =>
  requestJson<AudioTrackLabelSuggestionResult>(
    `/api/admin/audio-tracks/${encodeURIComponent(input.audioTrackId)}/label-suggestions`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ style_prompt: input.style_prompt })
    }
  )

export const suggestAudioLabelsFromPrompt = async (input: {
  style_prompt: string
}): Promise<AudioTrackLabelSuggestionResult> =>
  requestJson<AudioTrackLabelSuggestionResult>(
    '/api/admin/audio-tracks/label-suggestions',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ style_prompt: input.style_prompt })
    }
  )
